import React, { useMemo } from 'react';
import { Component, ComponentType } from '@/types';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LevelIndicatorThemeConfig {
  fillColor: string;
  emptyColor: string;
  shape: 'rect' | 'circle';
  gap: number;
  border?: boolean;
  glow?: boolean;
  gradientFill?: boolean;
  signalBars?: boolean;
}

interface LevelIndicatorProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, LevelIndicatorThemeConfig> = {
  battery: {
    fillColor: '#22c55e',
    emptyColor: '#333',
    shape: 'rect',
    gap: 2,
    border: true,
  },
  signal: {
    fillColor: '#4a90d9',
    emptyColor: CONTROL_THEME.muted,
    shape: 'rect',
    gap: 2,
    signalBars: true,
  },
  led: {
    fillColor: '#00ff88',
    emptyColor: '#1a1a2e',
    shape: 'circle',
    gap: 3,
    glow: true,
  },
  gradient: {
    fillColor: '#22c55e',
    emptyColor: CONTROL_THEME.muted,
    shape: 'rect',
    gap: 2,
    gradientFill: true,
  },
  minimal: {
    fillColor: CONTROL_THEME.mutedForeground,
    emptyColor: CONTROL_THEME.muted,
    shape: 'rect',
    gap: 1,
  },
};

// ─── Gradient Color Helper ───────────────────────────────────────────────────

function getGradientColor(segIndex: number, totalSegments: number): string {
  const ratio = segIndex / Math.max(totalSegments - 1, 1);
  // Green → Yellow → Red (from full to empty, i.e. high index = green)
  if (ratio > 0.5) {
    const t = (ratio - 0.5) * 2;
    const r = Math.round(255 * (1 - t));
    const g = Math.round(200 + 55 * t);
    return `rgb(${r}, ${g}, 30)`;
  } else {
    const t = ratio * 2;
    const r = 255;
    const g = Math.round(200 * t);
    return `rgb(${r}, ${g}, 30)`;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export const LevelIndicator: React.FC<LevelIndicatorProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // ─── Property Extraction ─────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'battery';
  const segments = Math.max(2, Math.min((properties.segments as number) ?? 10, 50));
  const orientation = (properties.orientation as string) ?? 'vertical';
  const showValue = (properties.showValue as boolean) ?? false;
  const showPercentage = (properties.showPercentage as boolean) ?? false;
  const label = (properties.label as string) ?? '';
  const unitStr = (properties.unit as string) ?? '';
  const min = (properties.min as number) ?? 0;
  const max = (properties.max as number) ?? 100;
  const warningThreshold = properties.warningThreshold as number | undefined;
  const criticalThreshold = properties.criticalThreshold as number | undefined;
  const isHorizontal = orientation === 'horizontal';

  // ─── Theme Resolution ────────────────────────────────────────────────
  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['battery'];
    return {
      ...base,
      ...(properties.fillColor != null && { fillColor: properties.fillColor as string }),
      ...(properties.emptyColor != null && { emptyColor: properties.emptyColor as string }),
    };
  }, [themeName, properties.fillColor, properties.emptyColor]);

  const warningColor = (properties.warningColor as string) ?? '#f59e0b';
  const criticalColor = (properties.criticalColor as string) ?? '#ef4444';

  // ─── Data Binding ────────────────────────────────────────────────────
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueField as string) ?? 'value';

  const { value: rawValue } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : (properties.value as string | number | undefined),
    templateId,
  });

  const propertyValue = (properties.value as number) ?? 75;
  const currentValue = useMemo(() => {
    if (dataSource && rawValue !== null) {
      const parsed = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      if (!isNaN(parsed)) return Math.min(max, Math.max(min, parsed));
    }
    return Math.min(max, Math.max(min, propertyValue));
  }, [dataSource, rawValue, propertyValue, min, max]);

  // ─── Computed ────────────────────────────────────────────────────────
  const percentage = max > min ? ((currentValue - min) / (max - min)) * 100 : 0;
  const filledSegments = Math.round((percentage / 100) * segments);

  // Threshold color: all filled segments use the same color based on value
  const fillColor = useMemo(() => {
    if (criticalThreshold != null && currentValue <= criticalThreshold) return criticalColor;
    if (warningThreshold != null && currentValue <= warningThreshold) return warningColor;
    return theme.fillColor;
  }, [currentValue, criticalThreshold, warningThreshold, criticalColor, warningColor, theme.fillColor]);

  // ─── Signal Theme (special layout) ───────────────────────────────────
  if (theme.signalBars) {
    const barCount = segments;
    return (
      <div
        data-testid="level-indicator"
        role="meter"
        aria-valuenow={currentValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={label || 'Level Indicator'}
        style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', gap: 2, padding: 4, boxSizing: 'border-box' }}
      >
        {label && <span style={{ fontSize: 10, color: CONTROL_THEME.mutedForeground, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{label}</span>}
        <svg width="100%" height="100%" viewBox={`0 0 ${barCount * 10 + (barCount - 1) * theme.gap} 40`} preserveAspectRatio="xMidYMax meet" style={{ flex: 1 }}>
          {Array.from({ length: barCount }, (_, i) => {
            const barHeight = ((i + 1) / barCount) * 36 + 4;
            const isFilled = i < filledSegments;
            const x = i * (10 + theme.gap);
            return (
              <rect
                key={i}
                x={x}
                y={40 - barHeight}
                width={10}
                height={barHeight}
                rx={2}
                fill={isFilled ? fillColor : theme.emptyColor}
              />
            );
          })}
        </svg>
        {(showValue || showPercentage) && (
          <span style={{ fontSize: 11, fontWeight: 600, color: CONTROL_THEME.foreground, fontVariantNumeric: 'tabular-nums' }}>
            {showValue && `${currentValue.toFixed(0)}${unitStr ? ` ${unitStr}` : ''}`}
            {showValue && showPercentage && ' '}
            {showPercentage && `${percentage.toFixed(0)}%`}
          </span>
        )}
      </div>
    );
  }

  // ─── Standard Vertical/Horizontal Layout ──────────────────────────────
  const segSize = theme.shape === 'circle' ? 10 : isHorizontal ? 10 : 6;
  const totalLen = segments * segSize + (segments - 1) * theme.gap;
  const crossSize = theme.shape === 'circle' ? 10 : isHorizontal ? 20 : 16;

  const svgWidth = isHorizontal ? totalLen : crossSize + (theme.border ? 4 : 0);
  const svgHeight = isHorizontal ? crossSize + (theme.border ? 4 : 0) : totalLen;
  const offset = theme.border ? 2 : 0;

  return (
    <div
      data-testid="level-indicator"
      role="meter"
      aria-valuenow={currentValue}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={label || 'Level Indicator'}
      style={{
        display: 'flex',
        flexDirection: isHorizontal ? 'column' : 'row',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 4,
        boxSizing: 'border-box',
      }}
    >
      {label && <span style={{ fontSize: 10, color: CONTROL_THEME.mutedForeground, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(isHorizontal ? { maxWidth: '100%' } : { writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: '80%' }) }}>{label}</span>}

      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ flex: 1 }}
      >
        {/* Battery border */}
        {theme.border && (
          <rect
            x={0.5}
            y={0.5}
            width={svgWidth - 1}
            height={svgHeight - 1}
            rx={3}
            fill="none"
            stroke={CONTROL_THEME.mutedForeground}
            strokeWidth={1}
          />
        )}

        {/* Segments */}
        {Array.from({ length: segments }, (_, i) => {
          const isFilled = i < filledSegments;
          const segFill = isFilled
            ? (theme.gradientFill ? getGradientColor(i, segments) : fillColor)
            : theme.emptyColor;

          if (theme.shape === 'circle') {
            const cx = isHorizontal
              ? offset + 5 + i * (10 + theme.gap)
              : offset + crossSize / 2;
            const cy = isHorizontal
              ? offset + crossSize / 2
              : svgHeight - offset - 5 - i * (10 + theme.gap);
            return (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={4}
                fill={segFill}
                {...(theme.glow && isFilled ? { filter: 'url(#glow)' } : {})}
              />
            );
          }

          // Rect segments
          const x = isHorizontal
            ? offset + i * (segSize + theme.gap)
            : offset;
          const y = isHorizontal
            ? offset
            : svgHeight - offset - (i + 1) * segSize - i * theme.gap;
          const w = isHorizontal ? segSize : crossSize;
          const h = isHorizontal ? crossSize : segSize;

          return (
            <rect key={i} x={x} y={y} width={w} height={h} rx={1} fill={segFill} />
          );
        })}

        {/* LED glow filter */}
        {theme.glow && (
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        )}
      </svg>

      {(showValue || showPercentage) && (
        <span style={{ fontSize: 11, fontWeight: 600, color: CONTROL_THEME.foreground, fontVariantNumeric: 'tabular-nums', textAlign: 'center' }}>
          {showValue && `${currentValue.toFixed(0)}${unitStr ? ` ${unitStr}` : ''}`}
          {showValue && showPercentage && ' '}
          {showPercentage && `${percentage.toFixed(0)}%`}
        </span>
      )}
    </div>
  );
};
